import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, FileSpreadsheet, FileText, RefreshCcw } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import {
  EbdClassDiaryReport,
  EbdMonthlyReport,
  EbdReportTotals,
  EbdStudentsAttendanceReport,
  EbdWeeklyReport,
  ebdReportsService,
} from '../services/ebdReports.service';
import { AppReportId } from '../services/report.service';

interface Props {
  quarterId: string;
  reportId: AppReportId;
}

const formatDate = (value: string) => new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR');
const formatMonth = (value: string) =>
  new Date(`${value}-01T00:00:00`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

const totalsToRows = (totals: EbdReportTotals) => [
  ['Bíblias', totals.bibles_count],
  ['Presentes', totals.present_count],
  ['Ausentes', totals.absent_count],
  ['Visitantes', totals.visitors_count],
  ['Turmas', totals.classes_count],
  ['Aulas', totals.lessons_count],
];

const downloadCsv = (filename: string, rows: Array<Array<string | number>>) => {
  const body = rows.map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';')).join('\n');
  const blob = new Blob([`\uFEFF${body}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 250);
};

const downloadXls = (filename: string, rows: Array<Array<string | number>>) => {
  const escapeCell = (value: string | number) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const htmlRows = rows
    .map((line) => `<tr>${line.map((cell) => `<td>${escapeCell(cell)}</td>`).join('')}</tr>`)
    .join('');
  const html = `<html><head><meta charset="UTF-8"></head><body><table>${htmlRows}</table></body></html>`;
  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 250);
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 250);
};

const EbdReportsHubView: React.FC<Props> = ({ quarterId, reportId }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [weekly, setWeekly] = useState<EbdWeeklyReport | null>(null);
  const [monthly, setMonthly] = useState<EbdMonthlyReport | null>(null);
  const [diary, setDiary] = useState<EbdClassDiaryReport | null>(null);
  const [studentsAttendance, setStudentsAttendance] = useState<EbdStudentsAttendanceReport | null>(null);
  const usesQuarterOnly = reportId === 'students-attendance' || reportId === 'honor-merit';
  const isHonorMeritReport = reportId === 'honor-merit';
  const hidePeriodFilters = reportId === 'students-attendance' || reportId === 'honor-merit';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const effectiveDateFrom = usesQuarterOnly ? undefined : dateFrom || undefined;
      const effectiveDateTo = usesQuarterOnly ? undefined : dateTo || undefined;
      const [w, m, d, s] = await Promise.all([
        ebdReportsService.weekly(quarterId, effectiveDateFrom, effectiveDateTo),
        ebdReportsService.monthly(quarterId, effectiveDateFrom, effectiveDateTo),
        ebdReportsService.classDiary(quarterId, effectiveDateFrom, effectiveDateTo),
        ebdReportsService.studentsAttendance(quarterId),
      ]);
      setWeekly(w);
      setMonthly(m);
      setDiary(d);
      setStudentsAttendance(s);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, quarterId, usesQuarterOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const activeData = useMemo(() => {
    if (reportId === 'weekly') return weekly;
    if (reportId === 'monthly') return monthly;
    if (reportId === 'class-diary') return diary;
    if (reportId === 'students-attendance' || reportId === 'honor-merit') return studentsAttendance;
    return null;
  }, [diary, monthly, reportId, studentsAttendance, weekly]);

  const handleExcel = () => {
    if (isHonorMeritReport) return;
    if (!activeData) return;

    if (reportId === 'class-diary' && diary) {
      const rows: Array<Array<string | number>> = [
        ['Data', 'Congregação', 'Segmento', 'Turma', 'Lição', 'Bíblias', 'Presentes', 'Ausentes', 'Visitantes'],
        ...diary.entries.map((entry) => [
          formatDate(entry.taught_on),
          entry.church.name,
          entry.segment.name,
          entry.class.name,
          `${entry.lesson.number} - ${entry.lesson.title}`,
          entry.bibles_count,
          entry.present_count,
          entry.absent_count,
          entry.visitors_count,
        ]),
      ];
      downloadCsv('diario-classe.csv', rows);
      return;
    }

    if (reportId === 'students-attendance' && studentsAttendance) {
      const rows: Array<Array<string | number>> = [
        ['Congregação', 'Tipo', 'Aluno', 'Turma', 'Presentes', 'Ausentes', 'Aulas', 'Frequência (%)'],
      ];

      studentsAttendance.churches.forEach((church) => {
        church.top_present.forEach((student) => {
          rows.push([
            church.church_name,
            'Mais presentes',
            student.student_name,
            student.class_name,
            student.present_count,
            student.absent_count,
            student.total_lessons,
            student.attendance_rate,
          ]);
        });

        church.absentees.forEach((student) => {
          rows.push([
            church.church_name,
            'Faltosos',
            student.student_name,
            student.class_name,
            student.present_count,
            student.absent_count,
            student.total_lessons,
            student.attendance_rate,
          ]);
        });
      });

      downloadXls('alunos-presenca-faltosos.xls', rows);
      return;
    }

    const groups = (activeData as EbdWeeklyReport | EbdMonthlyReport).groups;
    const rows: Array<Array<string | number>> = [
      ['Congregação', 'Segmento', 'Turma', 'Bíblias', 'Presentes', 'Ausentes', 'Visitantes'],
    ];
    groups.forEach((church) => {
      church.segments.forEach((segment) => {
        segment.classes.forEach((cls) => {
          rows.push([
            church.church_name,
            segment.segment_name,
            cls.class_name,
            cls.totals.bibles_count,
            cls.totals.present_count,
            cls.totals.absent_count,
            cls.totals.visitors_count,
          ]);
        });
      });
    });
    downloadCsv(`${reportId}.csv`, rows);
  };

  const handlePdf = async () => {
    try {
      const blob = await ebdReportsService.downloadPdf(
        reportId,
        quarterId,
        usesQuarterOnly ? undefined : dateFrom || undefined,
        usesQuarterOnly ? undefined : dateTo || undefined
      );
      downloadBlob(blob, `${reportId}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error('Falha ao baixar PDF do backend', error);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          {!hidePeriodFilters && (
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2"
              disabled={usesQuarterOnly}
            />
          )}
          {!hidePeriodFilters && (
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2"
              disabled={usesQuarterOnly}
            />
          )}
          <Button type="button" onClick={load} className="h-11">
            <RefreshCcw size={16} className="mr-2" />Atualizar
          </Button>
          <div className="flex gap-2">
            {!isHonorMeritReport && (
              <Button type="button" variant="outline" className="h-11 flex-1" onClick={handleExcel}>
                <FileSpreadsheet size={16} className="mr-2" />Excel
              </Button>
            )}
            <Button type="button" variant="outline" className="h-11 flex-1" onClick={handlePdf}>
              <FileText size={16} className="mr-2" />PDF
            </Button>
          </div>
        </div>
        {usesQuarterOnly && (
          <p className="mt-3 text-xs text-slate-500">
            Este relatório considera automaticamente todo o trimestre selecionado.
          </p>
        )}
      </Card>

      {activeData && 'totals' in activeData && activeData.totals && (
        <Card className="border-slate-200 bg-white p-4">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
            {totalsToRows(activeData.totals).map(([label, value]) => (
              <div key={String(label)} className="rounded-xl bg-slate-50 p-3">
                <p className="text-xs text-slate-500">{label}</p>
                <p className="text-xl font-bold text-slate-900">{value}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {reportId === 'weekly' && weekly && (
        <Card className="border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-lg font-semibold text-slate-900">Resumo semanal</h3>
          <div className="space-y-2">
            {weekly.weeks.map((week) => (
              <div key={week.week_key} className="rounded-xl border border-slate-200 p-3 text-sm">
                <div className="font-semibold text-slate-800">
                  {formatDate(week.starts_on)} a {formatDate(week.ends_on)}
                </div>
                <div className="text-slate-600">
                  Presentes: {week.totals.present_count} | Ausentes: {week.totals.absent_count} | Visitantes:{' '}
                  {week.totals.visitors_count} | Bíblias: {week.totals.bibles_count}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {reportId === 'monthly' && monthly && (
        <Card className="border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-lg font-semibold text-slate-900">Resumo mensal</h3>
          <div className="space-y-2">
            {monthly.months.map((month) => (
              <div key={month.month} className="rounded-xl border border-slate-200 p-3 text-sm">
                <div className="font-semibold text-slate-800">{formatMonth(month.month)}</div>
                <div className="text-slate-600">
                  Presentes: {month.totals.present_count} | Ausentes: {month.totals.absent_count} | Visitantes:{' '}
                  {month.totals.visitors_count} | Bíblias: {month.totals.bibles_count}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {reportId === 'students-attendance' && studentsAttendance && (
        <Card className="border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-lg font-semibold text-slate-900">Mais presentes e faltosos por congregação</h3>
          <div className="space-y-4">
            {studentsAttendance.churches.map((church) => (
              <div key={church.church_id} className="rounded-xl border border-slate-200 p-3">
                <h4 className="mb-2 text-base font-bold text-slate-900">{church.church_name}</h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <p className="mb-1 text-sm font-semibold text-emerald-700">Alunos mais presentes</p>
                    <ul className="space-y-1 text-sm text-slate-700">
                      {church.top_present.map((student) => (
                        <li key={`p-${student.student_id}`}>
                          {student.student_name} ({student.class_name}) - {student.attendance_rate}%
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="mb-1 text-sm font-semibold text-rose-700">Alunos faltosos</p>
                    <ul className="space-y-1 text-sm text-slate-700">
                      {church.absentees.map((student) => (
                        <li key={`a-${student.student_id}`}>
                          {student.student_name} ({student.class_name}) - {student.absent_count} faltas
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {reportId === 'honor-merit' && studentsAttendance && (
        <Card className="border-slate-200 bg-white p-4">
          <div className="mb-2 flex items-center gap-2 text-slate-700">
            <Download size={16} /> Honra ao Mérito por congregação
          </div>
          <p className="text-sm text-slate-600">
            Gere o PDF para imprimir. O documento sai com página separadora para cada congregação e certificados no modelo do anexo.
          </p>
          <div className="mt-3 space-y-2">
            {studentsAttendance.churches.map((church) => (
              <div key={church.church_id} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                {church.church_name}: {church.honor_students.length} aluno(s) com 100% de presença
              </div>
            ))}
          </div>
        </Card>
      )}

      {reportId === 'class-diary' && diary && (
        <Card className="border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-lg font-semibold text-slate-900">Diário de classe (todas as turmas)</h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500">
                  <th className="px-2 py-2">Data</th>
                  <th className="px-2 py-2">Congregação</th>
                  <th className="px-2 py-2">Segmento</th>
                  <th className="px-2 py-2">Turma</th>
                  <th className="px-2 py-2">Lição</th>
                  <th className="px-2 py-2">Bíblias</th>
                  <th className="px-2 py-2">Presentes</th>
                  <th className="px-2 py-2">Ausentes</th>
                  <th className="px-2 py-2">Visitantes</th>
                </tr>
              </thead>
              <tbody>
                {diary.entries.map((entry) => (
                  <tr key={`${entry.class.id}-${entry.lesson.id}-${entry.taught_on}`} className="border-t border-slate-100">
                    <td className="px-2 py-2">{formatDate(entry.taught_on)}</td>
                    <td className="px-2 py-2">{entry.church.name}</td>
                    <td className="px-2 py-2">{entry.segment.name}</td>
                    <td className="px-2 py-2">{entry.class.name}</td>
                    <td className="px-2 py-2">
                      {entry.lesson.number} - {entry.lesson.title}
                    </td>
                    <td className="px-2 py-2">{entry.bibles_count}</td>
                    <td className="px-2 py-2">{entry.present_count}</td>
                    <td className="px-2 py-2">{entry.absent_count}</td>
                    <td className="px-2 py-2">{entry.visitors_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {loading && <div className="text-sm text-slate-500">Carregando relatórios...</div>}
      {!loading && !activeData && <div className="text-sm text-slate-500">Nenhum dado encontrado.</div>}

      {!loading && (reportId === 'weekly' || reportId === 'monthly') && activeData && 'groups' in activeData && (
        <Card className="border-slate-200 bg-white p-4">
          <div className="mb-2 flex items-center gap-2 text-slate-700">
            <Download size={16} /> Agrupado por congregação, segmento e turma
          </div>
          <div className="space-y-3">
            {activeData.groups.map((church) => (
              <div key={church.church_id} className="rounded-xl border border-slate-200 p-3">
                <h4 className="font-semibold text-slate-900">{church.church_name}</h4>
                {church.segments.map((segment) => (
                  <div key={segment.segment_id} className="mt-2 rounded-lg bg-slate-50 p-2">
                    <p className="text-sm font-semibold text-slate-800">{segment.segment_name}</p>
                    <ul className="mt-1 text-sm text-slate-600">
                      {segment.classes.map((cls) => (
                        <li key={cls.class_id}>
                          {cls.class_name}: Presentes {cls.totals.present_count} | Ausentes {cls.totals.absent_count} | Visitantes{' '}
                          {cls.totals.visitors_count} | Bíblias {cls.totals.bibles_count}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default EbdReportsHubView;
